// TUT-460 — as telas do app, carregadas sob demanda (`lazy`), e o
// `Suspense` que as envolve. Cada uma vira um chunk próprio no build.
import { Suspense, lazy } from "react";
import type { ReactNode } from "react";
import { PageLoading } from "../components/PageState";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

// Cada página é carregada sob demanda (code-splitting por rota): o bundle
// inicial deixa de embarcar todas as telas, então a primeira abertura do tenant
// baixa só o framework + a rota atual. AppShell e PageState ficam ansiosos
// (fazem parte do casco e do fallback). Ver docs/design/ui-foundation.md.
export const AcceptInvitationPage = lazy(() =>
  import("../pages/AcceptInvitationPage").then((m) => ({
    default: m.AcceptInvitationPage,
  })),
);

// TUT-261 — o formulario de login ganha ENDERECO. Ate aqui ele so existia
// renderizado no lugar de uma rota do escritorio (ver `semSessao`, em
// `App.tsx`), o que continua valendo para quem chega deslogado numa tela de
// trabalho. O que faltava era um destino para "Acessar o app" da pagina
// inicial — e um lugar para as provas de e2e entrarem, agora que `/` mostra a
// pagina publica.
export const LoginPage = lazy(() =>
  import("../pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);

export const ForgotPasswordPage = lazy(() =>
  import("../pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);

export const ResetPasswordPage = lazy(() =>
  import("../pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);

export const PublicProposalResponsePage = lazy(() =>
  import("../pages/PublicProposalResponsePage").then((m) => ({
    default: m.PublicProposalResponsePage,
  })),
);

// TUT-541 — o pedido de proposta pelo site, sem login.
export const PedirPropostaPage = lazy(() =>
  import("../pages/PedirPropostaPage").then((m) => ({
    default: m.PedirPropostaPage,
  })),
);

// Console da plataforma Breviti (TUT-069). Fica num chunk próprio, carregado
// SOMENTE na rota do Console Brevi: nenhuma rota de tenant baixa este código, e
// nenhuma tela do tenant referencia ou linka esta rota (invisibilidade — C1).
export const PlatformConsolePage = lazy(() =>
  import("../pages/platform/PlatformConsolePage").then((m) => ({
    default: m.PlatformConsolePage,
  })),
);

export const CadastrosPage = lazy(() =>
  import("../pages/CadastrosPage").then((m) => ({ default: m.CadastrosPage })),
);

export const ConversasPage = lazy(() =>
  import("../pages/ConversasPage").then((m) => ({
    default: m.ConversasPage,
  })),
);

// TUT-537 — a biblioteca de Defesas.
export const DefesasPage = lazy(() =>
  import("../pages/DefesasPage").then((m) => ({ default: m.DefesasPage })),
);

export const NormasPage = lazy(() =>
  import("../pages/NormasPage").then((m) => ({ default: m.NormasPage })),
);

// TUT-517 — a central de notificações da pessoa.
export const NotificacoesPage = lazy(() =>
  import("../pages/NotificacoesPage").then((m) => ({ default: m.NotificacoesPage })),
);

export const FeedbacksPage = lazy(() =>
  import("../pages/FeedbacksPage").then((m) => ({ default: m.FeedbacksPage })),
);

export const ConfiguracoesPage = lazy(() =>
  import("../pages/ConfiguracoesPage").then((m) => ({
    default: m.ConfiguracoesPage,
  })),
);

export const AdvogadosPage = lazy(() =>
  import("../pages/AdvogadosPage").then((m) => ({ default: m.AdvogadosPage })),
);

export const AgendaPage = lazy(() =>
  import("../pages/AgendaPage").then((m) => ({ default: m.AgendaPage })),
);

export const DashboardPage = lazy(() =>
  import("../pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);

export const SpecialWorksPage = lazy(() =>
  import("../pages/SpecialWorksPage").then((m) => ({
    default: m.SpecialWorksPage,
  })),
);

export const ProposalsPage = lazy(() =>
  import("../pages/ProposalsPage").then((m) => ({ default: m.ProposalsPage })),
);

export const ProposalAssistancePage = lazy(() =>
  import("../pages/ProposalAssistancePage").then((m) => ({
    default: m.ProposalAssistancePage,
  })),
);

export const ProposalConsultingPage = lazy(() =>
  import("../pages/ProposalConsultingSpecialPage").then((m) => ({
    default: m.ProposalConsultingPage,
  })),
);

export const ProposalSpecialPage = lazy(() =>
  import("../pages/ProposalConsultingSpecialPage").then((m) => ({
    default: m.ProposalSpecialPage,
  })),
);

export const FrotaPage = lazy(() =>
  import("../pages/FrotaPage").then((m) => ({ default: m.FrotaPage })),
);

export const ViagensPage = lazy(() =>
  import("../pages/ViagensPage").then((m) => ({ default: m.ViagensPage })),
);

export const LocadorasPage = lazy(() =>
  import("../pages/LocadorasPage").then((m) => ({ default: m.LocadorasPage })),
);

export const FornecedoresPage = lazy(() =>
  import("../pages/FornecedoresPage").then((m) => ({ default: m.FornecedoresPage })),
);

export const ReportsPage = lazy(() =>
  import("../pages/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);

export const FinanceiroPage = lazy(() =>
  import("../pages/FinanceiroPage").then((m) => ({ default: m.FinanceiroPage })),
);

export const AccountsPayablePage = lazy(() =>
  import("../pages/AccountsPayablePage").then((m) => ({ default: m.AccountsPayablePage })),
);

export const AccountsReceivablePage = lazy(() =>
  import("../pages/AccountsReceivablePage").then((m) => ({ default: m.AccountsReceivablePage })),
);

export const ConsultoriaPage = lazy(() =>
  import("../pages/ConsultoriaPage").then((m) => ({
    default: m.ConsultoriaPage,
  })),
);

export const DocumentsPage = lazy(() =>
  import("../pages/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
);

export const PerfilPage = lazy(() =>
  import("../pages/PerfilPage").then((m) => ({ default: m.PerfilPage })),
);

// TUT-536 (D10) — os peritos do juízo, em tela própria (Processos › Peritos do juízo).
export const PeritosDoJuizoPage = lazy(() =>
  import("../pages/PeritosDoJuizoPage").then((m) => ({ default: m.PeritosDoJuizoPage })),
);

export const PeritosPage = lazy(() =>
  import("../pages/PeritosPage").then((m) => ({ default: m.AssistentesPage })),
);

export const PessoasPage = lazy(() =>
  import("../pages/PessoasPage").then((m) => ({ default: m.PessoasPage })),
);

export const AjudaPage = lazy(() =>
  import("../pages/AjudaPage").then((m) => ({ default: m.AjudaPage })),
);

export const ProcessDetailPage = lazy(() =>
  import("../pages/ProcessDetailPage").then((m) => ({
    default: m.ProcessDetailPage,
  })),
);

export const ProcessesPage = lazy(() =>
  import("../pages/ProcessesPage").then((m) => ({ default: m.ProcessesPage })),
);

export const ElaboracaoPage = lazy(() =>
  import("../pages/ElaboracaoPage").then((m) => ({
    default: m.ElaboracaoPage,
  })),
);

export const LegalBriefsPage = lazy(() =>
  import("../pages/LegalBriefsPage").then((m) => ({
    default: m.LegalBriefsPage,
  })),
);

export const ExpertisesPage = lazy(() =>
  import("../pages/ExpertisesPage").then((m) => ({ default: m.ExpertisesPage })),
);

// TUT-568 — a vista Lista do Agendamento.
export const AgendamentoListaPage = lazy(() =>
  import("../pages/AgendamentoListaPage").then((m) => ({ default: m.AgendamentoListaPage })),
);

export const AgendamentoPage = lazy(() =>
  import("../pages/AgendamentoPage").then((m) => ({
    default: m.AgendamentoPage,
  })),
);

// Envolve um nó lazy numa fronteira de Suspense com o loading padrão — para as
// rotas FORA do TenantGate, que é quem provê a fronteira das rotas do tenant.
export function suspense(node: ReactNode) {
  return <Suspense fallback={<PageLoading />}>{node}</Suspense>;
}

// A tela de Conversas (antes, a de Atendimento) recebe a sessão como prop. Antes o App a passava do
// próprio estado; com a árvore como objeto, a rota lê a MESMA query de sessão
// do TenantGate — cache quente, sem requisição nova — e só monta a página com
// o dado presente (o portão garante que está).
export function ConversasComSessao() {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    retry: false,
    staleTime: 60_000,
  });
  if (!session.data) return null;
  return <ConversasPage session={session.data} />;
}
